import React from 'react';
import { Box, Dialog, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography, useTheme } from '@mui/material';
import { UserFetchJson } from '../userFetch';

// Platform-operator server status strip (header, left of the clock). Polls
// /api/server/health every 10s; the endpoint is answered from an in-process
// sampler and never waits on the DB, so a wedged DB shows as a red DB tile
// rather than a hung widget. A failed poll turns the whole strip red.

const POLL_MS = 10_000;
const REQUEST_TIMEOUT_MS = 8_000;

type Sample = { t: number; cpu: number; memPct: number; poolWaiting: number; loopLagMs: number };
type Health = {
  now: number;
  host: { uptimeSec: number; loadAvg: number[]; cpus: number };
  process: { uptimeSec: number; rssMB: number; heapUsedMB: number; maxRssMB: number; node: string };
  cpu: { current: number; max: number };
  memory: { totalMB: number; availMB: number; usedMB: number; swapTotalMB: number; swapUsedMB: number };
  disk: { totalGB: number; usedGB: number; pct: number };
  cert?: { ok: boolean; host: string; validTo: string; daysLeft: number; issuer: string; checkedAt: number; error: string };
  db: { ok: boolean; latencyMs: number; error: string; pool: { total: number; idle: number; waiting: number } };
  ws: { devices: number; users: number; builder: number; total: number };
  loopLagMs: number;
  activeAlerts: { key: string; since: number }[];
  history: Sample[];
};
type ServerEvent = { id: number; timestamp: string; level: string; state: string; key: string; summary: string; detail: string | null };

type Level = 'ok' | 'warn' | 'crit';
const RANK: Record<Level, number> = { ok: 0, warn: 1, crit: 2 };
const worst = (...l: Level[]) => l.reduce((a, b) => (RANK[b] > RANK[a] ? b : a), 'ok');

function fmtUptime(sec: number) {
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60);
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function Sparkline({ values, max, color }: { values: number[]; max: number; color: string }) {
  const w = 44, h = 16;
  if (values.length < 2) return <svg width={w} height={h} />;
  const step = w / (values.length - 1);
  const pts = values.map((v, i) => (i * step).toFixed(1) + ',' + (h - 1 - Math.min(v, max) / max * (h - 2)).toFixed(1)).join(' ');
  return (
    <svg width={w} height={h} aria-hidden="true">
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function Tile({ label, value, level, detail, spark }: {
  label: string; value: string; level: Level; detail: string; spark?: React.ReactNode
}) {
  const theme = useTheme();
  const dot = level === 'crit' ? theme.palette.error.main : level === 'warn' ? theme.palette.warning.main : theme.palette.success.main;
  return (
    <Tooltip title={<Box sx={{ whiteSpace: 'pre-line', fontFamily: 'monospace', fontSize: 12 }}>{detail}</Box>} arrow>
      <Box sx={{ textAlign: 'right', minWidth: 52, cursor: 'default' }}>
        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, lineHeight: 1.2, color: 'text.secondary' }}>
          <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: dot, display: 'inline-block' }} />
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
          {spark}
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500, lineHeight: 1.3 }}>{value}</Typography>
        </Box>
      </Box>
    </Tooltip>
  );
}

function EventsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const theme = useTheme();
  const [events, setEvents] = React.useState<ServerEvent[] | null>(null);
  const [err, setErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setEvents(null); setErr(null);
    UserFetchJson('/api/server/events?limit=200').then(setEvents).catch(e => setErr(e?.message || 'failed'));
  }, [open]);

  const levelColor = (lvl: string, state: string) =>
    state === 'recovered' ? theme.palette.success.main : lvl === 'CRIT' ? theme.palette.error.main
      : lvl === 'INFO' ? theme.palette.info.main : theme.palette.warning.main;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ py: 1.5 }}>Server events</DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {err && <Typography color="error" sx={{ p: 2 }}>{err}</Typography>}
        {!err && events && events.length === 0 && <Typography sx={{ p: 2, color: 'text.secondary' }}>No server events recorded.</Typography>}
        {!err && events && events.length > 0 && (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Time (local)</TableCell>
                <TableCell>Level</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Event</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map(ev => (
                <Tooltip key={ev.id} title={<Box sx={{ whiteSpace: 'pre-line', fontFamily: 'monospace', fontSize: 12 }}>{ev.detail || ''}</Box>} placement="bottom-start">
                  <TableRow hover>
                    <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{new Date(ev.timestamp).toLocaleString('en-CA', { hour12: false })}</TableCell>
                    <TableCell>
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: levelColor(ev.level, ev.state), display: 'inline-block' }} />
                        {ev.level}
                      </Box>
                    </TableCell>
                    <TableCell>{ev.state}</TableCell>
                    <TableCell>{ev.summary}</TableCell>
                  </TableRow>
                </Tooltip>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ServerHealth() {
  const theme = useTheme();
  const [health, setHealth] = React.useState<Health | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [eventsOpen, setEventsOpen] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), REQUEST_TIMEOUT_MS);
      try {
        const h = await UserFetchJson('/api/server/health', { signal: ctrl.signal });
        if (!cancelled) { setHealth(h); setError(null); }
      } catch (e: any) {
        if (!cancelled) setError(e?.name === 'AbortError' ? 'no answer in 8s' : (e?.message || 'unreachable'));
      } finally {
        clearTimeout(timer);
      }
    };
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const sparkColor = theme.palette.text.secondary;

  if (!health) {
    return (
      <Box sx={{ mr: 2, px: 1, borderRadius: 1, border: 1, borderColor: error ? 'error.main' : 'divider' }}>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: error ? 'error.main' : 'text.secondary' }}>
          {error ? 'SERVER: ' + error : 'server...'}
        </Typography>
      </Box>
    );
  }

  const { cpu, memory: mem, disk, db, ws, process: proc, host, cert } = health;
  const hist = health.history;
  const nl = String.fromCharCode(10);

  const cpuLevel: Level = cpu.current > 80 ? 'crit' : cpu.current > 60 ? 'warn' : 'ok';
  const memLevel: Level = mem.availMB < 150 ? 'crit' : (mem.availMB < 300 || mem.swapUsedMB > 300) ? 'warn' : 'ok';
  const diskLevel: Level = disk.pct > 90 ? 'crit' : disk.pct > 80 ? 'warn' : 'ok';
  const dbLevel: Level = !db.ok || db.pool.waiting > 5 ? 'crit' : (db.latencyMs > 500 || db.pool.waiting > 0) ? 'warn' : 'ok';
  const loopLevel: Level = health.loopLagMs > 1000 ? 'crit' : health.loopLagMs > 200 ? 'warn' : 'ok';
  // certbot renews at 30 days left; under 25 = renewal running late, under 20 = server CRIT alert.
  const certLevel: Level = !cert ? 'ok' : (cert.validTo && cert.daysLeft < 20) ? 'crit' : (!cert.ok || cert.daysLeft < 25) ? 'warn' : 'ok';
  const overall = error ? 'crit' : worst(cpuLevel, memLevel, diskLevel, dbLevel, loopLevel, certLevel);
  const borderColor = overall === 'crit' ? 'error.main' : overall === 'warn' ? 'warning.main' : 'divider';
  const activeCount = health.activeAlerts?.length || 0;

  return (<>
    <EventsDialog open={eventsOpen} onClose={() => setEventsOpen(false)} />
    <Box onClick={() => setEventsOpen(true)} title="Click for server event history"
      sx={{ display: 'flex', gap: 2, alignItems: 'center', mr: 2, px: 1.5, py: 0.25, borderRadius: 1, border: 1, borderColor, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
      {activeCount > 0 && (
        <Tooltip title={health.activeAlerts.map(a => a.key + ' since ' + new Date(a.since).toLocaleTimeString('en-CA', { hour12: false })).join(', ')}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'error.main' }}>{activeCount} ALERT{activeCount > 1 ? 'S' : ''}</Typography>
        </Tooltip>
      )}
      <Tile label="CPU" value={Math.round(cpu.current) + '%'} level={cpuLevel}
        spark={<Sparkline values={hist.map(s => s.cpu)} max={100} color={sparkColor} />}
        detail={[
          'CPU ' + cpu.current + '% now, max ' + cpu.max + '% since start',
          'load ' + host.loadAvg.join(' / ') + ' on ' + host.cpus + ' vCPU',
          'event-loop lag ' + health.loopLagMs + ' ms' + (loopLevel !== 'ok' ? '  (!)' : ''),
          'sparkline: last ' + Math.round(hist.length / 6) + ' min'
        ].join(nl)} />
      <Tile label="MEM" value={mem.usedMB + '/' + mem.totalMB} level={memLevel}
        spark={<Sparkline values={hist.map(s => s.memPct)} max={100} color={sparkColor} />}
        detail={[
          'host: ' + mem.usedMB + ' MB used, ' + mem.availMB + ' MB available of ' + mem.totalMB,
          'swap: ' + mem.swapUsedMB + '/' + mem.swapTotalMB + ' MB',
          'node: RSS ' + proc.rssMB + ' MB (max ' + proc.maxRssMB + ' since start), heap ' + proc.heapUsedMB + ' MB'
        ].join(nl)} />
      <Tile label="DISK" value={disk.pct + '%'} level={diskLevel}
        detail={disk.usedGB + ' / ' + disk.totalGB + ' GB used'} />
      <Tile label="DB" value={db.ok ? db.latencyMs + 'ms' : 'DOWN'} level={dbLevel}
        spark={<Sparkline values={hist.map(s => s.poolWaiting)} max={10} color={sparkColor} />}
        detail={[
          db.ok ? 'SELECT 1 in ' + db.latencyMs + ' ms' : 'FAILED: ' + db.error,
          'pool: ' + db.pool.total + ' conns, ' + db.pool.idle + ' idle, ' + db.pool.waiting + ' waiting',
          'sparkline: queries waiting for a connection'
        ].join(nl)} />
      {cert && (
        <Tile label="CERT" value={cert.validTo ? cert.daysLeft + 'd' : '?'} level={certLevel}
          detail={[
            cert.validTo ? 'TLS cert for ' + cert.host + ' valid to ' + cert.validTo.slice(0, 10) + ' (' + cert.daysLeft + ' days)' : 'no successful check yet',
            cert.issuer ? 'issuer: ' + cert.issuer : '',
            cert.validTo ? 'certbot renews at 30 days left (~' + new Date(new Date(cert.validTo).getTime() - 30 * 86400000).toISOString().slice(0, 10) + ')' : '',
            cert.ok ? 'checked ' + new Date(cert.checkedAt).toLocaleTimeString('en-CA', { hour12: false }) + ', hourly' : 'LAST CHECK FAILED: ' + cert.error
          ].filter(Boolean).join(nl)} />
      )}
      <Tile label="WS" value={ws.devices + '/' + ws.users} level="ok"
        detail={ws.devices + ' devices, ' + ws.users + ' users' + (ws.builder ? ', builder' : '') + ' connected'} />
      <Tile label="UP" value={fmtUptime(proc.uptimeSec)} level="ok"
        detail={[
          'node process up ' + fmtUptime(proc.uptimeSec) + ' (' + proc.node + ')',
          'host up ' + fmtUptime(host.uptimeSec)
        ].join(nl)} />
    </Box>
  </>);
}

export default ServerHealth;
